/**
 * Transforma os números do painel (dominio/painel.ts) no que os gráficos
 * desenham: linhas das barras, carros da corrida, escala do eixo, tabela e
 * notas de rodapé. Não faz conta de negócio nenhuma, só arruma para mostrar.
 *
 * Lê: um PainelMes.
 * Usado por: telas/Painel.tsx.
 */
import { mesDaData } from '../dominio/datas.ts';
import { fechamentoEspecial } from '../dominio/fechamento.ts';
import { SITUACOES_DA_CARTEIRA, SUPERMETA_PERCENTUAL } from '../dominio/regras.ts';
import type { LinhaPainel, PainelMes } from '../dominio/painel.ts';
import { formatarData, formatarDataCurta, formatarMoeda, formatarPorcentagem, iniciais, listar, NOME_DO_SISTEMA } from '../formatacao.ts';
import type { ItemBarra, MarcadorBarra } from '../graficos/GraficoBarras.tsx';
import type { ItemCorrida } from '../graficos/CorridaCarrinhos.tsx';
import type { TabelaDoGrafico } from '../componentes/CartaoGrafico.tsx';
import { arredondarPara, marcasDoEixo, passoDoEixo } from '../graficos/svg.ts';

/** O que cada gráfico mede: % da meta, participação no faturamento ou carteira atendida. */
export type Medida = 'atingimento' | 'participacao' | 'carteira';

/** Representantes em ordem do maior para o menor; quem tem zero vai para o fim, em ordem alfabética. */
export function ordenar(linhas: LinhaPainel[], medida: Medida): LinhaPainel[] {
  return [...linhas].sort((a, b) => {
    const va = a[medida] ?? -1;
    const vb = b[medida] ?? -1;
    if (vb !== va) return vb - va;
    return a.representante.nome.localeCompare(b.representante.nome, 'pt-BR');
  });
}

function detalhes(linha: LinhaPainel, medida: Medida): string[] {
  if (medida === 'carteira') {
    if (linha.carteira === null) return ['Sem clientes na carteira', linha.representante.nome, `${linha.clientes} clientes compraram no mês`];
    return [
      `${formatarPorcentagem(linha.carteira)} da carteira atendida`,
      linha.representante.nome,
      `${linha.clientes} de ${linha.clientesNaCarteira} clientes da carteira compraram no mês`,
    ];
  }
  if (medida === 'atingimento') {
    if (linha.meta === 0) return [`Sem meta no mês`, linha.representante.nome, `Faturado: ${formatarMoeda(linha.liquido)}`];
    return [
      `${formatarPorcentagem(linha.atingimento)} da meta`,
      linha.representante.nome,
      `Faturado: ${formatarMoeda(linha.liquido)} de ${formatarMoeda(linha.meta)}`,
      `Falta ${formatarMoeda(linha.faltaParaMeta)} · projeção ${formatarPorcentagem(linha.atingimentoProjetado)}`,
    ];
  }
  return [
    `${formatarPorcentagem(linha.participacao)} do faturamento da equipe`,
    linha.representante.nome,
    `${formatarMoeda(linha.liquido)} · ${linha.notas} notas · ${linha.clientes} clientes`,
  ];
}

function texto(linha: LinhaPainel, medida: Medida): string {
  if (medida === 'atingimento' && linha.meta === 0) return 'sem meta';
  if (medida === 'carteira' && linha.carteira === null) return 'sem carteira';
  return formatarPorcentagem(linha[medida]);
}

export function itensDeBarras(painel: PainelMes, medida: Medida, abrir: (id: number) => void): ItemBarra[] {
  const itens: ItemBarra[] = ordenar(painel.linhas, medida).map((linha) => ({
    chave: String(linha.representante.id),
    rotulo: linha.representante.nome,
    iniciais: iniciais(linha.representante.nome),
    foto: linha.representante.foto ?? null,
    valor: linha[medida] ?? 0,
    texto: texto(linha, medida),
    apagado: (linha[medida] ?? 0) === 0,
    detalhes: detalhes(linha, medida),
    aoAbrir: () => abrir(linha.representante.id),
  }));
  if (medida === 'atingimento') {
    itens.push({
      chave: 'equipe',
      rotulo: 'Equipe',
      valor: painel.equipe.atingimento ?? 0,
      texto: formatarPorcentagem(painel.equipe.atingimento),
      destaque: true,
      detalhes: [
        `${formatarPorcentagem(painel.equipe.atingimento)} da meta da equipe`,
        `Faturado: ${formatarMoeda(painel.equipe.liquido)} de ${formatarMoeda(painel.equipe.meta)}`,
        `Projeção: ${formatarPorcentagem(painel.equipe.atingimentoProjetado)} da meta`,
      ],
    });
  }
  return itens;
}

export function itensDaCorrida(painel: PainelMes, medida: Medida, abrir: (id: number) => void): ItemCorrida[] {
  let posicao = 0;
  return ordenar(painel.linhas, medida).map((linha) => {
    const valor = linha[medida] ?? 0;
    const andou = valor > 0;
    if (andou) posicao++;
    return {
      chave: String(linha.representante.id),
      nome: linha.representante.nome,
      valor,
      texto: andou ? texto(linha, medida) : medida === 'atingimento' && linha.meta === 0 ? 'sem meta' : 'na largada',
      iniciais: iniciais(linha.representante.nome),
      foto: linha.representante.foto ?? null,
      posicao: andou ? posicao : null,
      detalhes: detalhes(linha, medida),
      aoAbrir: () => abrir(linha.representante.id),
    };
  });
}

/** Escala do gráfico de barras da meta: trilho até 100%, com espaço para a linha da supermeta (105%). */
export function escalaDaMeta(painel: PainelMes): { maximo: number; marcas: number[]; marcadores: MarcadorBarra[] } {
  const maior = Math.max(0, ...painel.linhas.map((l) => l.atingimento ?? 0));
  const maximo = Math.max(1.2, arredondarPara(maior * 1.05, 0.25));
  const marcadores: MarcadorBarra[] = [{ valor: 1 + SUPERMETA_PERCENTUAL, rotulo: `Supermeta ${formatarPorcentagem(1 + SUPERMETA_PERCENTUAL, 0)}`, tipo: 'supermeta' }];
  if (painel.ritmo !== null) marcadores.unshift({ valor: painel.ritmo, rotulo: `Ritmo hoje ${formatarPorcentagem(painel.ritmo, 0)}`, tipo: 'ritmo' });
  return { maximo, marcas: marcasDoEixo(Math.min(maximo, 1), 0.25), marcadores };
}

/** Escala da corrida e da participação: até um pouco depois do maior valor (e do ritmo). */
export function escalaAjustada(valores: number[], ritmo: number | null): { maximo: number; marcas: number[] } {
  const maior = Math.max(0.1, ...valores, ritmo ?? 0);
  const passo = passoDoEixo(maior * 1.08);
  const maximo = arredondarPara(maior * 1.08, passo);
  return { maximo, marcas: marcasDoEixo(maximo, passo) };
}

export function tabelaDoGrafico(painel: PainelMes, medida: Medida): TabelaDoGrafico {
  const linhas = ordenar(painel.linhas, medida).map((linha) =>
    medida === 'atingimento'
      ? [linha.representante.nome, formatarMoeda(linha.liquido), formatarMoeda(linha.meta), formatarPorcentagem(linha.atingimento), formatarMoeda(linha.projecao ?? 0), formatarPorcentagem(linha.atingimentoProjetado)]
      : [linha.representante.nome, formatarMoeda(linha.liquido), String(linha.notas), String(linha.clientes), formatarPorcentagem(linha.participacao)],
  );
  if (medida === 'atingimento') {
    const e = painel.equipe;
    linhas.push(['Equipe', formatarMoeda(e.liquido), formatarMoeda(e.meta), formatarPorcentagem(e.atingimento), formatarMoeda(e.projecao ?? 0), formatarPorcentagem(e.atingimentoProjetado)]);
    return { colunas: ['Representante', 'Faturado (líquido)', 'Meta', '% da meta', 'Projeção', '% projetado'], linhas, numericas: [1, 2, 3, 4, 5] };
  }
  if (medida === 'carteira') {
    const linhasDaCarteira = ordenar(painel.linhas, medida).map((linha) => [
      linha.representante.nome,
      String(linha.clientes),
      linha.clientesNaCarteira === null ? '—' : String(linha.clientesNaCarteira),
      formatarPorcentagem(linha.carteira),
    ]);
    const e = painel.equipe;
    linhasDaCarteira.push(['Equipe', String(e.clientes), e.clientesNaCarteira === null ? '—' : String(e.clientesNaCarteira), formatarPorcentagem(e.carteira)]);
    return { colunas: ['Representante', 'Compraram no mês', 'Clientes na carteira', '% da carteira'], linhas: linhasDaCarteira, numericas: [1, 2, 3] };
  }
  return { colunas: ['Representante', 'Faturado (líquido)', 'Notas', 'Clientes', 'Participação'], linhas, numericas: [1, 2, 3, 4] };
}

/** "de 01 a 09/10" para títulos ("de 01/09 a 01/10" quando o mês fecha em outro mês). */
export function periodoDosDados(painel: PainelMes): string {
  if (painel.dataReferencia === null) return '(ainda sem vendas importadas)';
  const { inicio } = painel.periodo;
  const doInicio = mesDaData(inicio) === mesDaData(painel.dataReferencia) ? inicio.slice(8, 10) : formatarDataCurta(inicio);
  return `de ${doInicio} a ${formatarDataCurta(painel.dataReferencia)}`;
}


/** De onde vieram os números (arquivos por marca, arquivo geral ou SAP) e o período do mês. */
export function notaDaOrigem(painel: PainelMes): string {
  const { inicio, fim } = painel.periodo;
  const fechamento = fechamentoEspecial(painel.fonte, painel.mes) ? ` Este mês vai de ${formatarDataCurta(inicio)} a ${formatarDataCurta(fim)} (fechamento combinado).` : '';
  if (painel.fonte === 'sap-bauny') {
    return `Faturamento = notas de venda do SAP (Utilização "Venda", valor após desconto). Bonificação, provador, display e devolução ficam de fora.${fechamento}`;
  }
  if (painel.origem.modo === 'por-marca') {
    const marcas = painel.origem.marcas.map((m) => `${m.marca.nome} (até ${formatarDataCurta(m.ultimaData)})`);
    return `Faturamento = soma dos arquivos do Winthor por marca: ${listar(marcas)}.${fechamento}`;
  }
  const foraDaConta = painel.origem.vendasDeixadasDeFora > 0 ? ` Arquivos de marca inativa (${painel.origem.vendasDeixadasDeFora} notas) ficaram fora da conta.` : '';
  return `O arquivo do Winthor é geral (todas as marcas juntas): o faturamento inclui tudo que foi faturado no Winthor. Para ver por marca, importe um arquivo por marca.${foraDaConta}${fechamento}`;
}

export function notasDoGrafico(painel: PainelMes, medida: Medida): string[] {
  if (medida === 'carteira') {
    const usada = painel.carteiraUsada;
    return [
      `Carteira atendida = clientes que compraram no mês (líquido maior que zero) ÷ clientes da carteira do representante (situação ${listar([...SITUACOES_DA_CARTEIRA])}).`,
      usada
        ? `Carteira de clientes do Winthor de ${formatarData(usada.dataReferencia)}.${usada.clientesSemDono > 0 ? ` ${usada.clientesSemDono} clientes estão sem representante (código de quem saiu, ou vazio).` : ''}`
        : 'Nenhuma carteira importada.',
      notaDaOrigem(painel),
    ];
  }
  const listaMarcas = listar(painel.marcasDaMeta.map((m) => m.nome));
  const ritmo = painel.ritmo === null ? null : `Ritmo hoje: ${painel.dias.passados} de ${painel.dias.total} dias úteis (${formatarPorcentagem(painel.ritmo, 0)}).`;
  if (medida === 'atingimento') {
    return [
      `Atingido = faturamento líquido (vendas − devoluções) ÷ meta do mês das marcas do ${NOME_DO_SISTEMA[painel.fonte]}: ${listaMarcas}.`,
      [ritmo, `Supermeta = meta + ${formatarPorcentagem(SUPERMETA_PERCENTUAL, 0)}.`].filter(Boolean).join(' '),
      notaDaOrigem(painel),
    ];
  }
  return [
    `Base: faturamento líquido dos representantes do ranking (${formatarMoeda(painel.equipe.liquido)}). O sócio e quem vende sem meta ficam fora da conta.`,
    notaDaOrigem(painel),
  ];
}
